/**
 * A ponte de WhatsApp — contrato e textos.
 *
 * A ponte é uma aplicação **separada** (ver `docs/ponte-whatsapp.md`) que fala
 * o protocolo do WhatsApp e alimenta esta API. Este arquivo é a fronteira entre
 * as duas: se um campo mudar aqui, os dois lados quebram no mesmo commit.
 *
 * Duas decisões que o formato do contrato carrega:
 *
 * 1. **Todo o tráfego é ponte → API.** Não há rota que a API chame na ponte.
 *    Ela busca o que fazer em `outbox` e responde. É o que permite a ponte
 *    rodar atrás de qualquer NAT, sem porta aberta, sem IP fixo — e é o que
 *    torna irrelevante onde ela está hospedada.
 *
 * 2. **A ponte só lê.** Não existe contrato de envio de mensagem, de propósito.
 *    O `CLAUDE.md` é explícito sobre aniversário — "o botão de mandar é o mesmo
 *    de sempre: o app não manda nada sozinho" — e é justamente o envio
 *    automático que derruba número no WhatsApp. Com a sessão viva, "manda a
 *    confirmação sozinho" viraria uma linha; não haver contrato para isso é o
 *    que mantém a tentação longe.
 */

import { z } from 'zod';
import { idSchema, instantSchema, phoneSchema } from './common.js';
import { imagePayloadSchema } from './media.js';

// ── Parâmetros ──────────────────────────────────────────────────────────────
//
// Não são regra de agenda: nenhum barbeiro tem opinião sobre eles, e duas
// barbearias não os querem diferentes. Ficam aqui, não no `.env` e não em
// `TenantSettings`. O que é por barbearia é ligar ou não a ponte.

/**
 * Quanto tempo a foto em cache vale antes de valer buscar de novo.
 *
 * Foto de perfil muda, mas devagar. Curto demais desfaz o cache e multiplica as
 * consultas ao WhatsApp — que é exatamente o volume que se quer manter baixo.
 */
export const CONTACT_PHOTO_MAX_AGE_DAYS = 30;

/**
 * Quanto tempo um rascunho que nunca virou nada fica guardado.
 *
 * Entram no banco pessoas que nunca tiveram nada com a barbearia; a minimização
 * é por tempo. Noventa dias cobrem o cliente sazonal — quem corta o cabelo a
 * cada dois meses — sem guardar para sempre o número errado que mandou "oi".
 */
export const CONTACT_DRAFT_RETENTION_DAYS = 90;

/**
 * Quanto se espera por uma foto pedida antes de perguntar de novo — e antes de
 * concluir que o contato não tem foto de perfil.
 *
 * É o mesmo número que a ponte usa para não repetir um pedido que já falhou.
 * Os dois lados precisam esperar a mesma coisa: com janelas diferentes, um
 * concluiria "não tem foto" enquanto o outro ainda nem tinha tentado.
 */
export const CONTACT_PHOTO_RETRY_MINUTES = 30;

/**
 * Quanto a folha de agendar espera uma foto pedida antes de desistir de
 * perguntar. Uma volta do `outbox` (`BRIDGE_POLL_SECONDS`) mais a busca e o
 * envio da imagem cabem com folga; o que não chegou nisso é, quase sempre,
 * contato sem foto de perfil.
 */
export const CONTACT_PHOTO_WAIT_SECONDS = 20;

/** Silêncio a partir do qual a tela passa a dizer que a ponte caiu. */
export const BRIDGE_SIGNAL_STALE_MINUTES = 5;

/**
 * A janela do `PairPhone` do whatsmeow. A ponte deve gerar o código logo depois
 * de conectar o websocket, para o barbeiro receber os 160 segundos inteiros.
 */
export const PAIRING_CODE_TTL_SECONDS = 160;

/** De quanto em quanto tempo a ponte deve buscar o `outbox`. */
export const BRIDGE_POLL_SECONDS = 5;

/**
 * De quanto em quanto tempo a ponte deve mandar heartbeat, no máximo.
 *
 * Menor que `BRIDGE_SIGNAL_STALE_MINUTES` com folga, senão a tela acusa queda
 * de uma ponte que está de pé e só não falou ainda. O `outbox` **não** conta
 * como sinal de propósito: ele é buscado a cada poucos segundos e gravar a
 * marca ali seria uma escrita no banco por segundo para não dizer nada de novo.
 */
export const BRIDGE_HEARTBEAT_SECONDS = 60;

// ── Estado ──────────────────────────────────────────────────────────────────

export const whatsappBridgeStatusSchema = z.enum(['DISCONNECTED', 'PAIRING', 'CONNECTED']);

export type WhatsappBridgeStatus = z.infer<typeof whatsappBridgeStatusSchema>;

export const WHATSAPP_BRIDGE_STATUS_LABELS: Record<WhatsappBridgeStatus, string> = {
  DISCONNECTED: 'Desconectado',
  PAIRING: 'Esperando o código',
  CONNECTED: 'Conectado',
};

// ── O aviso ─────────────────────────────────────────────────────────────────

/**
 * O que o dono lê antes de ligar isto.
 *
 * A especificação decidiu "opcional, por barbearia, com aviso explícito"; este
 * é o aviso. As três frases não são jurídicas, são as três coisas que ele
 * precisa saber para a decisão ser dele de verdade — e a segunda é a que
 * ninguém conta: o recorte "só quem manda mensagem" é disciplina do nosso
 * código, não limite do acesso.
 */
export const BRIDGE_CONSENT_NOTICE = [
  'O WhatsApp não oferece isto oficialmente. Existe risco real de o número da barbearia ser suspenso, e não há como recorrer.',
  'A conexão enxerga o seu WhatsApp inteiro, não só quem chama a barbearia. O que o sistema guarda é apenas nome, telefone e foto de quem você procurar na busca.',
  'A barbearia não manda nenhuma mensagem sozinha. A conexão só lê contatos; quem escreve continua sendo você.',
] as const;

export const BRIDGE_CONSENT_CONFIRMATION = 'Entendi o risco e quero conectar';

// ── Dono → API ──────────────────────────────────────────────────────────────

export const enableBridgeSchema = z.object({
  /**
   * Sem isto a rota recusa. Não é formalidade: um `POST` vazio ligando a coisa
   * faria "aceitei o risco" acontecer por acidente, e o risco é o número do
   * negócio dele.
   */
  consent: z.literal(true, { message: 'É preciso aceitar o aviso para conectar' }),
});

export const pairBridgeSchema = z.object({
  /** O número que vai ficar vinculado — o WhatsApp da barbearia. */
  phone: phoneSchema,
});

export type EnableBridgeInput = z.input<typeof enableBridgeSchema>;
export type PairBridgeInput = z.input<typeof pairBridgeSchema>;

// ── Ponte → API ─────────────────────────────────────────────────────────────

/**
 * Os dois campos opcionais são `nullish`, e não `optional`, porque **limpar** é
 * uma operação legítima aqui: a ponte que voltou a funcionar precisa apagar o
 * erro anterior, senão a tela acende aviso sobre uma queda que já passou.
 *
 * Só `optional` recusaria `null` com 400, e o sintoma seria o pior possível —
 * uma ponte de pé, mandando heartbeat certinho, levando 400 em todos eles, e a
 * tela do dono dizendo "a conexão ainda não deu sinal de vida". Omitir o campo
 * e mandar `null` fazem a mesma coisa, de propósito: nenhuma das duas formas é
 * pegadinha para quem escrever a próxima ponte.
 */
export const bridgeHeartbeatSchema = z.object({
  status: whatsappBridgeStatusSchema,
  /** O número efetivamente vinculado, como a ponte o vê. */
  linkedPhone: phoneSchema.nullish(),
  /**
   * Último erro, em pt-BR e em linguagem de gente — ele vai aparecer na tela do
   * barbeiro. "Sessão expirada, pareie de novo" serve; um stack trace, não.
   */
  lastError: z.string().trim().max(300).nullish(),
});

export const reportPairingCodeSchema = z.object({
  /** O código do `PairPhone`, como o barbeiro vai digitar no aparelho. */
  code: z
    .string()
    .trim()
    .min(4, 'Código curto demais')
    .max(16, 'Código longo demais')
    .transform((value) => value.toUpperCase()),
  expiresAt: instantSchema,
});

/**
 * Um contato que mandou mensagem.
 *
 * Não existe rota para enviar a agenda inteira, e a ausência é o recurso: uma
 * carga em lote é indistinguível de raspagem e é o que faz o WhatsApp suspender
 * a conta. Um contato por mensagem recebida é o mesmo tráfego que o aplicativo
 * de verdade faz ao desenhar a conversa.
 */
export const ingestContactSchema = z.object({
  phone: phoneSchema,
  /** Como está salvo na agenda do barbeiro — é o nome que ele reconhece. */
  name: z.string().trim().min(1, 'Contato sem nome').max(120),
  whatsappJid: z.string().trim().max(120).optional(),
  /** Quando a mensagem chegou. Entra por parâmetro: a ponte tem o relógio. */
  messageAt: instantSchema,
});

export const ingestContactPhotoSchema = z.object({
  draftId: idSchema,
  data: imagePayloadSchema,
});

export type BridgeHeartbeatInput = z.input<typeof bridgeHeartbeatSchema>;
export type ReportPairingCodeInput = z.input<typeof reportPairingCodeSchema>;
export type IngestContactInput = z.input<typeof ingestContactSchema>;
export type IngestContactPhotoInput = z.input<typeof ingestContactPhotoSchema>;

/** O que a ponte encontra quando pergunta o que fazer. */
export interface BridgeOutboxView {
  /** Pareamento pedido pelo dono e ainda não atendido. */
  pairing: { phone: string; requestedAt: string } | null;
  /**
   * Fotos que valem buscar: só de contato que o barbeiro procurou e cuja foto
   * está ausente ou velha. Nunca a agenda inteira.
   */
  photoRequests: { draftId: string; phone: string; whatsappJid: string | null }[];
}

/**
 * O que a ponte da plataforma encontra ao perguntar quem ela atende.
 *
 * Só endereço, nunca credencial: o que autoriza é o token de plataforma que ela
 * já tem, e o que cada barbearia decide é ligar ou não o recurso. Devolver o
 * token da barbearia aqui pouparia uma configuração e transformaria esta rota
 * num vazamento de segredo de todas elas de uma vez.
 *
 * É a única rota da API que não pertence a uma barbearia — ela é chamada num
 * host onde não há tenant nenhum (`admin.<domínio>` serve).
 */
export interface BridgeFleetView {
  tenants: { slug: string; apiUrl: string }[];
}

// ── API → app do barbeiro ───────────────────────────────────────────────────

export interface WhatsappBridgeView {
  enabled: boolean;
  status: WhatsappBridgeStatus;
  linkedPhone: string | null;
  linkedPhoneFormatted: string | null;
  /** Código a mostrar na tela, se ainda estiver dentro da janela. */
  pairingCode: string | null;
  pairingExpiresAt: string | null;
  lastHeartbeatAt: string | null;
  /** Calculado com `isBridgeSignalStale` — a tela não recalcula prazo. */
  signalStale: boolean;
  lastError: string | null;
  consentAt: string | null;
  /** Últimos quatro do token em uso, para o dono saber qual está valendo. */
  tokenLastFour: string | null;
  /**
   * A plataforma hospeda a ponte?
   *
   * Ligado, o token não tem uso nenhum na vida do dono — ele só interessa a quem
   * for hospedar a própria ponte. Mostrá-lo em destaque faria o dono achar que
   * precisa fazer alguma coisa com aquilo, e a resposta certa é "nada".
   */
  platformBridge: boolean;
  /** Quantos contatos estão no rascunho, ainda sem virar ficha. */
  draftCount: number;
}

/**
 * O token de serviço, em claro.
 *
 * Aparece **uma vez**, na resposta que o cria. Depois disso só existe o hash —
 * mesma regra da senha. Se o dono perder, emite outro; não há como mostrar de
 * novo.
 */
export interface BridgeTokenView {
  token: string;
  lastFour: string;
}

/** Um contato do WhatsApp que ainda não é ficha. */
export interface ContactDraftView {
  id: string;
  name: string;
  phone: string;
  phoneFormatted: string;
  /** Nulo até a ponte trazer a foto — na primeira busca ela ainda não chegou. */
  photoUrl: string | null;
  /**
   * A foto ainda pode chegar nos próximos segundos. É o que a tela consulta
   * para decidir se pergunta de novo — `photoUrl` nulo não serve, porque
   * contato sem foto de perfil fica nulo para sempre.
   */
  photoPending: boolean;
  lastMessageAt: string;
}

export const promoteContactDraftSchema = z.object({
  /**
   * O nome da ficha, que é do barbeiro (ver `CLAUDE.md`). Vem preenchido com o
   * do WhatsApp, e é aqui que "João Pedro" vira "João Pedro do bigode" — a
   * única pista que faz ele achar a pessoa depois.
   */
  name: z.string().trim().min(2, 'Nome muito curto').max(120).optional(),
  notes: z.string().trim().max(1000).optional(),
});

export type PromoteContactDraftInput = z.input<typeof promoteContactDraftSchema>;
